// src/encounter/lib/referral-specialities.ts
// [PROD-3336] With the `referrals` switch on, a referral goes to one of the clinic's SPECIALITIES (it replaced the one
// hard-coded "role" row of PROD-1395). PURE: the list itself is read by services/use-org-specialities.ts.
//
// Dietitian keeps working at every step:
//   · a saved PROD-1395 referral carries only `performerRole: 'dietitian'`; it still opens, edits and saves as before;
//   · a speciality of the dietetics discipline still sends `performerRole: 'dietitian'`, so the readers that filter on it
//     (the Referral card's fallback, the clinical task text) still find it;
//   · when the org read fails or returns nothing, the pickers offer FALLBACK_SPECIALITIES (Dietitian).
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import { alliedDisciplineOf } from './allied-discipline';

export interface ReferralSpeciality {
  /** The speciality id (sent as `targetSpecialityId`), or LEGACY_DIETITIAN_ID for the fallback row. */
  id: string;
  /** Shown to the physician, and sent as `targetSpecialityName` (and `targetDisplay` when no practitioner is picked). */
  name: string;
  /** From the org read: an active practitioner holds this speciality. */
  hasActivePractitioner?: boolean;
}

/** The PROD-1395 role code. Sent as `performerRole` for a dietetics speciality, and the id of the fallback row. */
export const LEGACY_DIETITIAN_ID = 'dietitian';

/** The row offered when the org read fails or is empty: a referral to Dietitian, as before PROD-3336. */
export const FALLBACK_SPECIALITIES: readonly ReferralSpeciality[] = [{ id: LEGACY_DIETITIAN_ID, name: 'Dietitian' }];

/** True for the fallback / saved PROD-1395 dietitian target (no speciality id behind it). */
export function isLegacyDietitian(id: string | null | undefined): boolean {
  return (id ?? '').trim().toLowerCase() === LEGACY_DIETITIAN_ID;
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * True when a referral target's code names a speciality: a speciality id (a GUID) or the PROD-1395 dietitian. Any other code
 * (a procedure code on a legacy referral, for example CPT 97802) stays a plain referral target.
 */
export function isSpecialityTargetCode(code: string | null | undefined): boolean {
  const c = (code ?? '').trim();
  return isLegacyDietitian(c) || GUID.test(c);
}

/** The `performerRole` a referral to this speciality also sends: 'dietitian' for the dietetics discipline, else none. */
export function performerRoleOf(speciality: Pick<ReferralSpeciality, 'id' | 'name'>): string | undefined {
  return isLegacyDietitian(speciality.id) || alliedDisciplineOf(speciality.name) === 'dietetics' ? LEGACY_DIETITIAN_ID : undefined;
}

/** The list the pickers offer: the org's specialities, or the fallback when there are none. */
export function offeredSpecialities(fromOrg: readonly ReferralSpeciality[] | undefined): readonly ReferralSpeciality[] {
  return fromOrg && fromOrg.length > 0 ? fromOrg : FALLBACK_SPECIALITIES;
}

/** A search row for a speciality: id = code = speciality id, plus whether anyone can be picked under it. */
export type SpecialitySearchResult = CodeSearchResult & { hasActivePractitioner?: boolean };

/** The specialities whose name contains the query (all for an empty query), as search results. */
export function searchSpecialities(specialities: readonly ReferralSpeciality[], query: string): SpecialitySearchResult[] {
  const q = query.trim().toLowerCase();
  return specialities
    .filter((s) => !q || s.name.toLowerCase().includes(q))
    .map((s) => ({
      id: s.id,
      code: s.id,
      display: s.name,
      ...(s.hasActivePractitioner === undefined ? {} : { hasActivePractitioner: s.hasActivePractitioner }),
    }));
}

/** Where the add lines say a speciality row comes from. */
export const SPECIALITY_SOURCE = 'clinic specialities';
/** Said on a row whose speciality has no active practitioner (it can still be referred to, with no one to pick). */
export const NO_ACTIVE_PRACTITIONER = 'No active practitioner';

/**
 * The hint of a speciality row in the add lines. The id is a GUID, so it is never shown (other catalogues show their code);
 * a speciality with no active practitioner says so.
 */
export function specialityRowHint(item: unknown): string {
  const none = (item as SpecialitySearchResult | undefined)?.hasActivePractitioner === false;
  return none ? `${NO_ACTIVE_PRACTITIONER} · ${SPECIALITY_SOURCE}` : SPECIALITY_SOURCE;
}
